import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { IonCard, IonCardContent, IonIcon, IonText, IonButton, IonToggle, IonActionSheet, IonAlert } from '@ionic/react';
import { addOutline, ellipsisVertical, chevronForwardOutline } from 'ionicons/icons';
import type { Device } from '../../../domain/Device';
import {
  COMPONENT_KINDS,
  COMPONENT_LABEL,
  MULTI_CHANNEL_KINDS,
  componentDisplayName,
  type ComponentKind,
  type DeviceComponent,
} from '../../../domain/Component';
import { COMPONENT_ICON } from './ComponentPicker';
import './station.css';

interface Props {
  device: Device;
  error: string | null;
  onAdd: (kind: ComponentKind) => void;
  onRename: (component: DeviceComponent, name: string) => void;
  onToggle: (component: DeviceComponent, enabled: boolean) => void;
  onRemove: (component: DeviceComponent) => void;
}

const DETAIL_SLUG: Partial<Record<ComponentKind, string>> = { air: 'temperatura', pressure: 'presion', rain: 'lluvia', wind: 'viento' };

function detailPath(deviceId: string, c: DeviceComponent): string | null {
  if (c.kind === 'soil') return `/app/stations/${deviceId}/suelo/${c.channel}`;
  const slug = DETAIL_SLUG[c.kind];
  return slug ? `/app/stations/${deviceId}/${slug}` : null;
}

const KIND_ORDER = (k: ComponentKind) => COMPONENT_KINDS.indexOf(k);

// Lo que tiene enchufado la estación. Lo que se desactiva deja de mostrarse
// (los datos se siguen guardando); lo que manda la estación sin estar en la
// lista se agrega solo ("detectado").
const StationComponentsCard: React.FC<Props> = ({ device, error, onAdd, onRename, onToggle, onRemove }) => {
  const history = useHistory();
  const canEdit = device.access !== 'view';
  const [showAdd, setShowAdd] = useState(false);
  const [menuFor, setMenuFor] = useState<DeviceComponent | null>(null);
  const [renaming, setRenaming] = useState<DeviceComponent | null>(null);
  const [removing, setRemoving] = useState<DeviceComponent | null>(null);

  const components = [...device.components].sort((a, b) => KIND_ORDER(a.kind) - KIND_ORDER(b.kind) || a.channel - b.channel);
  const addable = COMPONENT_KINDS.filter((k) => MULTI_CHANNEL_KINDS.includes(k) || !components.some((c) => c.kind === k && c.enabled));

  return (
    <IonCard>
      <IonCardContent>
        <div className="station-section-head">
          <span>Sensores y válvulas</span>
          {canEdit && (
            <IonButton size="small" fill="clear" onClick={() => setShowAdd(true)}>
              <IonIcon icon={addOutline} slot="start" />
              Agregar
            </IonButton>
          )}
        </div>

        {components.length === 0 && (
          <IonText color="medium">
            <p style={{ fontSize: 13, margin: '0 0 8px' }}>
              Todavía no tiene nada configurado. Agregá lo que tiene enchufado, o esperá su primer mensaje: lo que mande
              se suma solo.
            </p>
          </IonText>
        )}

        {components.map((c) => {
          const path = c.enabled ? detailPath(device.id, c) : null;
          // Sin nombre propio ya dice qué es ("Sonda de suelo 2", "Sector 1"):
          // la segunda línea solo hace falta cuando el nombre lo tapa.
          const sub = c.name
            ? [COMPONENT_LABEL[c.kind], MULTI_CHANNEL_KINDS.includes(c.kind) ? `Canal ${c.channel}` : null].filter(Boolean).join(' · ')
            : '';
          return (
            <div className={`station-component ${c.enabled ? '' : 'disabled'}`} key={c.id}>
              <button className="station-component-main" disabled={!path} onClick={() => path && history.push(path)}>
                <IonIcon icon={COMPONENT_ICON[c.kind]} className="station-component-icon" />
                <span className="station-component-text">
                  <strong>{componentDisplayName(c)}</strong>
                  {sub && <small>{sub}</small>}
                </span>
                {path && <IonIcon icon={chevronForwardOutline} className="station-component-chevron" />}
              </button>
              {canEdit && (
                <>
                  <IonToggle
                    checked={c.enabled}
                    onIonChange={(e) => onToggle(c, e.detail.checked)}
                    aria-label={`${c.enabled ? 'Desactivar' : 'Activar'} ${componentDisplayName(c)}`}
                  />
                  <IonButton fill="clear" size="small" color="medium" onClick={() => setMenuFor(c)} aria-label="Más opciones">
                    <IonIcon icon={ellipsisVertical} slot="icon-only" />
                  </IonButton>
                </>
              )}
            </div>
          );
        })}

        {error && (
          <IonText color="danger">
            <p style={{ fontSize: 12.5 }}>{error}</p>
          </IonText>
        )}

      </IonCardContent>

      <IonActionSheet
        isOpen={showAdd}
        onDidDismiss={() => setShowAdd(false)}
        header="¿Qué le enchufaste?"
        buttons={[
          ...addable.map((kind) => ({
            text: components.some((c) => c.kind === kind) ? `${COMPONENT_LABEL[kind]} (otra)` : COMPONENT_LABEL[kind],
            handler: () => onAdd(kind),
          })),
          { text: 'Cancelar', role: 'cancel' },
        ]}
      />

      <IonActionSheet
        isOpen={menuFor !== null}
        onDidDismiss={() => setMenuFor(null)}
        header={menuFor ? componentDisplayName(menuFor) : ''}
        buttons={[
          { text: 'Renombrar', handler: () => setRenaming(menuFor) },
          { text: 'Quitar', role: 'destructive', handler: () => setRemoving(menuFor) },
          { text: 'Cancelar', role: 'cancel' },
        ]}
      />

      <IonAlert
        isOpen={renaming !== null}
        onDidDismiss={() => setRenaming(null)}
        header="Renombrar"
        message={renaming?.kind === 'soil' ? 'Por ejemplo "Lote 4 · 30 cm".' : renaming?.kind === 'valve' ? 'Por ejemplo "Invernadero".' : undefined}
        inputs={[{ name: 'name', type: 'text', value: renaming?.name ?? '', placeholder: renaming ? componentDisplayName({ ...renaming, name: null }) : '' }]}
        buttons={[
          { text: 'Cancelar', role: 'cancel' },
          { text: 'Guardar', handler: (data) => {
            if (renaming) onRename(renaming, data.name ?? '');
          },
        },
        ]}
      />

      <IonAlert
        isOpen={removing !== null}
        onDidDismiss={() => setRemoving(null)}
        header={`¿Quitar ${removing ? componentDisplayName(removing) : ''}?`}
        message={
          removing?.kind === 'valve'
            ? 'Deja de regar y se borra su horario. El historial de riegos se conserva.'
            : 'Deja de mostrarse. Si la estación lo sigue mandando, vuelve a aparecer solo — para ocultarlo, mejor desactivalo.'
        }
        buttons={[
          { text: 'Cancelar', role: 'cancel' },
          { text: 'Quitar', role: 'destructive', handler: () => {
            if (removing) onRemove(removing);
          },
        },
        ]}
      />
    </IonCard>
  );
};

export default StationComponentsCard;
